import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Eye, Gem, Target, Swords, Palette, Globe, Layers, Activity, Sparkles, Settings2, Trash2, Package, Mail, Pencil, Users, UserPlus, LayoutDashboard, Lightbulb, FolderKanban, Rocket, AlertTriangle, Search, Upload, ExternalLink, GitFork, FileText } from "lucide-react";
import EmailsTab from "@/components/EmailsTab";
import LogModule from "@/components/LogModule";
import SEODashboard from "@/components/SEODashboard";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import ActivityTab from "@/components/ActivityTab";
import EntityQuickSwitcher from "@/components/EntityQuickSwitcher";
import BrandOverviewTab from "@/components/Brand/BrandOverviewTab";
import BrandStrategyTab from "@/components/Brand/BrandStrategyTab";
import BrandCompetitorsTab from "@/components/Brand/BrandCompetitorsTab";
import BrandIdentityTab from "@/components/Brand/BrandIdentityTab";
import BrandDigitalTab from "@/components/Brand/BrandDigitalTab";
import BrandPillarsOpsTab from "@/components/Brand/BrandPillarsOpsTab";
import BrandPackagesTab from "@/components/Brand/BrandPackagesTab";
import BrandContentStrategyTab from "@/components/Brand/BrandContentStrategyTab";
import BrandContentBucketTab from "@/components/Brand/BrandContentBucketTab";
import BrandGtmTab from "@/components/Brand/BrandGtmTab";
import BrandProjectsTab from "@/components/Brand/BrandProjectsTab";
import AuditFooter from "@/components/AuditFooter";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import TeamRosterTab from "@/components/TeamRosterTab";
import { EntityDiagramsTab } from "@/components/diagrams/EntityDiagramsTab";
import { EntityDocumentsTab } from "@/components/documents/EntityDocumentsTab";

// Iter 236 · Brand sub-tab visibility metadata. Each entry declares:
//   • key         — tab value
//   • label       — display label
//   • icon        — lucide icon
//   • viewPerm    — permission gate for VISIBILITY (falsy = always visible)
//   • editPerm    — permission gate for the edit toolbars/inline saves
// Missing viewPerm means the tab shows regardless (e.g. Activity is baseline).
const BRAND_TABS = [
  { key: "overview",         label: "Overview",                  view: "brand.tab.overview.view",         edit: "brand.tab.overview.edit" },
  { key: "strategy",         label: "Strategy & SWOT",            view: "brand.tab.strategy.view",         edit: "brand.tab.strategy.edit" },
  { key: "competitors",      label: "Competitors",                view: "brand.tab.competitors.view",      edit: "brand.tab.competitors.edit" },
  { key: "identity",         label: "Voice & Visual",             view: "brand.tab.identity.view",         edit: "brand.tab.identity.edit" },
  { key: "digital",          label: "Digital",                    view: "brand.tab.digital.view",          edit: "brand.tab.digital.edit" },
  { key: "ads",              label: "Ads",                         view: null }, // Iter 253z-11 · Perf Marketing per-brand shortcut
  { key: "pillars",          label: "Pillars, Dates & Ops",       view: "brand.tab.pillars.view",          edit: "brand.tab.pillars.edit" },
  { key: "packages",         label: "Packages",                    view: "brand.tab.packages.view",         edit: "brand.tab.packages.edit" },
  { key: "content-strategy", label: "Content Strategy",           view: "brand.tab.content_strategy.view", edit: "brand.tab.content_strategy.edit" },
  { key: "content-bucket",   label: "Content Bucket",             view: "brand.tab.content_bucket.view",   edit: "brand.tab.content_bucket.edit" },
  { key: "gtm",              label: "GTM",                         view: "brand.tab.gtm.view",              edit: "brand.tab.gtm.edit" },
  { key: "projects",         label: "Projects",                    view: "brand.tab.projects.view",         edit: "brand.tab.projects.edit" },
  { key: "log",              label: "Log",                         view: "brand.tab.log.view",              edit: "brand.tab.log.edit" },
  { key: "seo",              label: "SEO",                         view: "brand.tab.seo.view",              edit: "brand.tab.seo.edit" },
  { key: "emails",           label: "Emails",                      view: "brand.tab.emails.view",           edit: "brand.tab.emails.edit" },
  { key: "activity",         label: "Activity",                    view: "brand.tab.activity.view" },
  { key: "team",             label: "Team",                        view: null },
  { key: "diagrams",         label: "Diagrams",                    view: null }, // R45 slice 2 — always visible; scope-filtered by backend
  { key: "documents",        label: "Documents",                   view: null }, // R45 slice 2 Wave 2 — always visible; scope-filtered by backend
];

const STATUS_TONES = { active: "border-emerald-500/40 text-emerald-700", paused: "border-amber-500/40 text-amber-700", archived: "border-border text-muted-foreground" };

export default function BrandDetail() {
  const { bid } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeWorkspaceId } = useAuth();
  const { can, ready: permsReady } = useWorkspacePerms();
  const [brand, setBrand] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [readMode, setReadMode] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");

  // Iter 236 · Filter tabs by view permission. If perms haven't loaded yet,
  // show all tabs (fail-open on hydrate) — the underlying data endpoints still
  // enforce backend-side auth. Once perms load, tabs the user can't view
  // disappear from the tablist.
  const visibleTabs = BRAND_TABS.filter((t) => !permsReady || !t.view || can(t.view));
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Iter 236 · If the URL-selected tab is now hidden by permissions, bounce to
  // the first visible tab so the user never lands on an empty pane.
  useEffect(() => {
    if (!permsReady || visibleTabs.length === 0) return;
    if (!visibleTabs.find((t) => t.key === activeTab)) {
      const first = visibleTabs[0]?.key || "overview";
      setActiveTab(first);
      const next = new URLSearchParams(searchParams);
      if (first === "overview") next.delete("tab"); else next.set("tab", first);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsReady, activeTab]);
  const changeTab = (v) => {
    setActiveTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    try {
      const r = await api.get(`/brands/${bid}`);
      setBrand(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load brand")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  const showTab = (key) => visibleTabs.some((t) => t.key === key);

  // R138bh · Fetch sibling brands on demand for the quick switcher.
  const fetchSiblingBrands = async () => {
    if (!activeWorkspaceId) return [];
    const { data } = await api.get(`/workspaces/${activeWorkspaceId}/brands?limit=500`);
    const rows = Array.isArray(data) ? data : (data?.rows || data?.brands || []);
    return rows.map((b) => ({
      id: b.id,
      name: b.name,
      subtitle: b.client_name || b.industry || "",
    }));
  };

  if (!brand) return <div className="px-10 py-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <>
      <div className="px-4 sm:px-6 md:px-10 pt-6">
        <Link to="/app/brands" className="text-sm label-eyebrow inline-flex items-center hover:text-primary" data-testid="brand-back-link">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to brands
        </Link>
      </div>
      <PageHeader
        eyebrow="Brand"
        title={brand.name}
        description={[brand.industry, brand.client_name && `Client: ${brand.client_name}`, brand.website].filter(Boolean).join(" · ") || "Brand management hub"}
        testid="brand-detail-header"
        actions={
          <div className="flex gap-2 items-center flex-wrap">
            <EntityQuickSwitcher
              currentId={bid}
              currentName={brand.name}
              fetchItems={fetchSiblingBrands}
              buildPath={(id) => `/app/brands/${id}`}
              label="Brand"
              testidBase="brand-switcher"
            />
            <Badge variant="outline" className={`rounded-none ${STATUS_TONES[brand.status] || ""}`} data-testid="brand-status-badge">{brand.status}</Badge>
            <Button
              variant="outline"
              className={`rounded-none ${readMode ? "bg-primary/10 border-primary/40" : ""}`}
              onClick={() => setReadMode((v) => !v)}
              title={readMode ? "Switch to edit mode" : "Switch to read mode — clean, no toolbars"}
              data-testid="brand-read-mode-toggle"
            >
              {readMode ? <><Pencil className="w-4 h-4 mr-2" /> Edit mode</> : <><Eye className="w-4 h-4 mr-2" /> Read mode</>}
            </Button>
            <Button variant="outline" className="rounded-none border-primary/40 text-primary" onClick={() => nav(`/app/brands/${bid}/cockpit`)} data-testid="brand-cockpit-btn">
              <LayoutDashboard className="w-4 h-4 mr-2" /> Cockpit
            </Button>
            <Button variant="outline" className="rounded-none border-violet-500/50 text-violet-600" onClick={() => setAiOpen(true)} data-testid="brand-ai-brief-btn">
              <Sparkles className="w-4 h-4 mr-2" /> AI brief from website
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => setSettingsOpen(true)} data-testid="brand-settings-btn"><Settings2 className="w-4 h-4" /></Button>
            <AuditTrailPanel collection="brands" recordId={bid} testid="brand-audit-trail" />
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-6">
        <AssignedMembersStrip brand={brand} onEdit={() => setSettingsOpen(true)} />
        <Tabs value={activeTab} onValueChange={changeTab} className="w-full">
          <TabsList className="rounded-none flex-wrap h-auto justify-start">
            {showTab("overview") && <TabsTrigger value="overview" className="rounded-none" data-testid="brand-tab-overview"><Gem className="w-3.5 h-3.5 mr-1.5" /> Overview</TabsTrigger>}
            {showTab("strategy") && <TabsTrigger value="strategy" className="rounded-none" data-testid="brand-tab-strategy"><Target className="w-3.5 h-3.5 mr-1.5" /> Strategy & SWOT</TabsTrigger>}
            {showTab("competitors") && <TabsTrigger value="competitors" className="rounded-none" data-testid="brand-tab-competitors"><Swords className="w-3.5 h-3.5 mr-1.5" /> Competitors · {(brand.competitors || []).length}</TabsTrigger>}
            {showTab("identity") && <TabsTrigger value="identity" className="rounded-none" data-testid="brand-tab-identity"><Palette className="w-3.5 h-3.5 mr-1.5" /> Voice & Visual</TabsTrigger>}
            {showTab("digital") && <TabsTrigger value="digital" className="rounded-none" data-testid="brand-tab-digital"><Globe className="w-3.5 h-3.5 mr-1.5" /> Digital</TabsTrigger>}
            {showTab("pillars") && <TabsTrigger value="pillars" className="rounded-none" data-testid="brand-tab-pillars"><Layers className="w-3.5 h-3.5 mr-1.5" /> Pillars, Dates & Ops</TabsTrigger>}
            {showTab("packages") && <TabsTrigger value="packages" className="rounded-none" data-testid="brand-tab-packages"><Package className="w-3.5 h-3.5 mr-1.5" /> Packages</TabsTrigger>}
            {showTab("content-strategy") && <TabsTrigger value="content-strategy" className="rounded-none" data-testid="brand-tab-content-strategy"><Lightbulb className="w-3.5 h-3.5 mr-1.5" /> Content Strategy</TabsTrigger>}
            {showTab("content-bucket") && <TabsTrigger value="content-bucket" className="rounded-none" data-testid="brand-tab-content-bucket"><Package className="w-3.5 h-3.5 mr-1.5" /> Content Bucket</TabsTrigger>}
            {showTab("gtm") && <TabsTrigger value="gtm" className="rounded-none" data-testid="brand-tab-gtm"><Rocket className="w-3.5 h-3.5 mr-1.5" /> GTM</TabsTrigger>}
            {showTab("projects") && <TabsTrigger value="projects" className="rounded-none" data-testid="brand-tab-projects"><FolderKanban className="w-3.5 h-3.5 mr-1.5" /> Projects</TabsTrigger>}
            {showTab("log") && <TabsTrigger value="log" className="rounded-none" data-testid="brand-tab-log"><AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Log</TabsTrigger>}
            {showTab("seo") && <TabsTrigger value="seo" className="rounded-none" data-testid="brand-tab-seo"><Search className="w-3.5 h-3.5 mr-1.5" /> SEO</TabsTrigger>}
            {showTab("emails") && <TabsTrigger value="emails" className="rounded-none" data-testid="brand-tab-emails"><Mail className="w-3.5 h-3.5 mr-1.5" /> Emails</TabsTrigger>}
            {showTab("activity") && <TabsTrigger value="activity" className="rounded-none" data-testid="brand-tab-activity"><Activity className="w-3.5 h-3.5 mr-1.5" /> Activity</TabsTrigger>}
            {showTab("team") && <TabsTrigger value="team" className="rounded-none" data-testid="brand-tab-team"><Users className="w-3.5 h-3.5 mr-1.5" /> Team</TabsTrigger>}
            {showTab("diagrams") && <TabsTrigger value="diagrams" className="rounded-none" data-testid="brand-tab-diagrams"><GitFork className="w-3.5 h-3.5 mr-1.5" /> Diagrams</TabsTrigger>}
            {showTab("documents") && <TabsTrigger value="documents" className="rounded-none" data-testid="brand-tab-documents"><FileText className="w-3.5 h-3.5 mr-1.5" /> Documents</TabsTrigger>}
          </TabsList>
          <TabsContent value="overview" className="pt-6"><BrandOverviewTab key={`o-${brand.updated_at}`} brand={brand} onSaved={load} readMode={readMode} /></TabsContent>
          <TabsContent value="strategy" className="pt-6"><BrandStrategyTab key={`s-${brand.updated_at}`} brand={brand} onSaved={load} readMode={readMode} /></TabsContent>
          <TabsContent value="competitors" className="pt-6"><BrandCompetitorsTab key={`c-${brand.updated_at}`} brand={brand} onSaved={load} readMode={readMode} /></TabsContent>
          <TabsContent value="identity" className="pt-6"><BrandIdentityTab key={`i-${brand.updated_at}`} brand={brand} onSaved={load} readMode={readMode} /></TabsContent>
          <TabsContent value="digital" className="pt-6"><BrandDigitalTab key={`d-${brand.updated_at}`} brand={brand} onSaved={load} readMode={readMode} /></TabsContent>
          <TabsContent value="ads" className="pt-6">
            {/* Iter 253z-11 · Per-brand shortcut to Performance Marketing hub filtered by this brand. */}
            <div className="border border-border p-6 text-center space-y-3" data-testid="brand-ads-tab">
              <div className="label-eyebrow">Performance Marketing</div>
              <div className="text-sm font-semibold">Meta, Google & LinkedIn ad performance for <b>{brand.name}</b></div>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                Open the full Performance Marketing hub filtered by this brand — dashboards, KPIs, campaign controls, anomaly alerts and scheduled reports live there.
              </p>
              <div className="flex items-center justify-center gap-2">
                <a href={`/app/performance/campaigns?brand=${bid}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary bg-primary text-primary-foreground text-xs" data-testid="brand-ads-open-campaigns">Open campaigns →</a>
                <a href={`/app/performance?brand=${bid}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-xs" data-testid="brand-ads-open-hub">Open dashboard →</a>
                <a href="/app/performance/accounts" className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-xs" data-testid="brand-ads-open-accounts">Link an ad account →</a>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="pillars" className="pt-6"><BrandPillarsOpsTab key={`p-${brand.updated_at}`} brand={brand} onSaved={load} readMode={readMode} /></TabsContent>
          <TabsContent value="packages" className="pt-6"><BrandPackagesTab brand={brand} /></TabsContent>
          <TabsContent value="content-strategy" className="pt-6"><BrandContentStrategyTab brand={brand} /></TabsContent>
          <TabsContent value="content-bucket" className="pt-6"><BrandContentBucketTab brand={brand} /></TabsContent>
          <TabsContent value="gtm" className="pt-6"><BrandGtmTab brand={brand} /></TabsContent>
          <TabsContent value="projects" className="pt-6"><BrandProjectsTab brand={brand} /></TabsContent>
          <TabsContent value="log" className="pt-6"><LogModule scope="brand" scopeId={bid} defaults={{ brand_id: bid, client_id: brand?.client_id }} testid="brand-log" /></TabsContent>
          <TabsContent value="seo" className="pt-6"><SEODashboard workspaceId={activeWorkspaceId} brand={brand} testid="brand-seo" /></TabsContent>
          <TabsContent value="emails" className="pt-6"><EmailsTab entityType="brand" entityId={bid} embedded /></TabsContent>
          <TabsContent value="activity" className="pt-6"><ActivityTab entityType="brand" entityId={bid} testid="brand-activity" /></TabsContent>
          <TabsContent value="team" className="pt-6"><TeamRosterTab scope="brand" wsId={activeWorkspaceId} recordId={bid} /></TabsContent>
          <TabsContent value="diagrams" className="pt-6"><EntityDiagramsTab entityType="brand" entityId={bid} entityName={brand?.name || "this brand"} /></TabsContent>
          <TabsContent value="documents" className="pt-6"><EntityDocumentsTab entityType="brand" entityId={bid} entityName={brand?.name || "this brand"} /></TabsContent>
        </Tabs>
        <AuditFooter record={brand} testid="brand-audit-footer" />
      </div>
      {aiOpen && <AiBriefDialog wsId={activeWorkspaceId} brand={brand} onClose={() => setAiOpen(false)} onApplied={load} />}
      {settingsOpen && <BrandSettingsDialog wsId={activeWorkspaceId} brand={brand} onClose={() => setSettingsOpen(false)} onSaved={load} />}
    </>
  );
}

function AiBriefDialog({ wsId, brand, onClose, onApplied }) {
  const [url, setUrl] = useState(brand.website || "");
  const [busy, setBusy] = useState(false);
  const [bgJob, setBgJob] = useState(null); // { job_id }

  // Poll background jobs so the user gets feedback in-dialog and we can
  // trigger a reload when the job completes.
  useEffect(() => {
    if (!bgJob?.job_id) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/workspaces/${wsId}/jobs/${bgJob.job_id}`);
        setBgJob((prev) => (prev ? { ...prev, ...data } : prev));
        if (["done", "partial"].includes(data.status)) {
          toast.success("AI brief applied — refresh to see updated tabs");
          await onApplied?.();
        } else if (data.status === "failed") {
          toast.error(`AI brief job failed: ${data?.result?.error || "unknown"}`);
        } else {
          setTimeout(tick, 4000);
        }
      } catch {
        setTimeout(tick, 6000);
      }
    };
    setTimeout(tick, 3000);
    return () => { cancelled = true; };
  }, [bgJob?.job_id, wsId, onApplied]);

  const runForeground = async () => {
    if (!url.trim()) { toast.error("Enter the brand's website URL"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/ai-brief`,
        { website_url: url.trim(), brand_id: brand.id, apply: true },
        { timeout: 90000 });
      toast.success(r.data.applied ? "AI brief drafted & applied to empty sections — review each tab" : "AI brief drafted");
      await onApplied?.();
      onClose();
    } catch (e) {
      // Iter 235 · If the sync call fails (timeout, 502, 504), auto-fallback
      // to a background job so the user's work is not lost.
      const status = e?.response?.status;
      const isTimeout = e?.code === "ECONNABORTED" || status === 504 || status === 502 || status === 503;
      if (isTimeout) {
        toast.info("AI is taking longer than expected — switching to background. Come back in a couple of minutes.");
        await runBackground();
      } else {
        toast.error(apiErrorMessage(e, "AI brief failed"));
      }
    } finally { setBusy(false); }
  };

  const runBackground = async () => {
    if (!url.trim()) { toast.error("Enter the brand's website URL"); return; }
    setBusy(true);
    try {
      // Ensure the brand doc has the website URL persisted first (bulk endpoint
      // reads website from the brand record).
      if (url.trim() !== (brand.website || "")) {
        try { await api.patch(`/workspaces/${wsId}/brands/${brand.id}`, { website: url.trim() }); }
        catch { /* not fatal — server will try what it has */ }
      }
      const { data } = await api.post(`/workspaces/${wsId}/brands/bulk-ai-brief`, {
        ids: [brand.id], overwrite: false,
      });
      setBgJob({ job_id: data.job_id, status: "pending" });
      toast.success("Running in background. Feel free to close this — we'll email you when it's ready.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't start background job"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="ai-brief-dialog">
        <DialogHeader><DialogTitle>AI brand brief</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">AI reads the website and drafts mission, positioning, USPs, SWOT, voice, personas and content pillars. Only <strong>empty</strong> sections are filled — nothing you wrote is overwritten.</p>
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Website URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://brand.com" className="h-10 rounded-none" data-testid="ai-brief-url" />
        </div>
        {bgJob?.job_id && (
          <div className="border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="ai-brief-bg-status">
            <div className="font-semibold mb-1">Running in background</div>
            <div>Status: <span className="font-mono">{bgJob.status || "pending"}</span> · Progress: {bgJob.done || 0}/{bgJob.total || 1}</div>
            <p className="text-muted-foreground mt-1">You can close this dialog and check the brand page later — the brief will apply automatically when done.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Close</Button>
          <Button
            variant="outline"
            onClick={runBackground}
            disabled={busy || !!bgJob?.job_id}
            className="rounded-none"
            data-testid="ai-brief-run-bg"
            title="Kick off a background job — good for slow sites or if you don't want to wait"
          >
            Run in background
          </Button>
          <Button
            onClick={runForeground}
            disabled={busy || !!bgJob?.job_id}
            className="rounded-none"
            data-testid="ai-brief-run"
          >
            {busy ? "Analyzing… (~30s)" : "Generate & apply now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignedMembersStrip({ brand, onEdit }) {
  const [memberMap, setMemberMap] = useState({});
  const ws = brand.workspace_id;
  const ids = brand.assigned_user_ids || [];
  useEffect(() => {
    if (!ws || ids.length === 0) return;
    api.get(`/workspaces/${ws}/audit/users?ids=${ids.join(",")}`)
      .then((r) => setMemberMap(r.data?.users || {}))
      .catch(() => {});
  }, [ws, ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="brand-assigned-strip">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
        <Users className="w-3 h-3" /> Assigned team
      </span>
      {ids.length === 0 ? (
        <span className="text-xs text-muted-foreground italic">No one assigned yet — this brand is only visible to managers.</span>
      ) : (
        ids.map((uid) => {
          const u = memberMap[uid] || { name: uid.slice(0, 6) };
          return (
            <span
              key={uid}
              className="inline-flex items-center gap-1.5 border border-border rounded-none px-2 py-0.5 text-xs bg-background hover:border-primary/50 transition"
              data-testid={`brand-assigned-chip-${uid}`}
              title={u.email || u.name}
            >
              <span className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center text-[8px] font-bold">
                {(u.name || "?").charAt(0).toUpperCase()}
              </span>
              <span className="truncate max-w-[140px]">{u.name}</span>
            </span>
          );
        })
      )}
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex items-center gap-1 border border-dashed border-border text-[11px] px-2 py-0.5 hover:border-primary hover:text-primary transition"
        data-testid="brand-assign-add-btn"
      >
        <UserPlus className="w-3 h-3" /> {ids.length === 0 ? "Assign members" : "Edit"}
      </button>
    </div>
  );
}

function BrandSettingsDialog({ wsId, brand, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: brand.name, industry: brand.industry || "", website: brand.website || "",
    logo_url: brand.logo_url || "", status: brand.status || "active",
    client_id: brand.client_id || "", project_ids: brand.project_ids || [],
    assigned_user_ids: brand.assigned_user_ids || [],
    // Iter 253v · Portal theming — the client-portal signup screen reads these.
    primary_color: brand.primary_color || "#0f172a",
    portal_tagline: brand.portal_tagline || "",
  });
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be ≤ 2 MB"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/workspaces/${wsId}/brands/${brand.id}/logo`, fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", r.data.logo_url);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setUploadingLogo(false); }
  };
  const openPortalPreview = () => {
    // Iter 253w · Open the themed client portal in a new tab so admins can
    // sanity-check the colour + logo + tagline without leaving the editor.
    window.open(`/client/portal?ws=${wsId}&brand=${brand.id}`, "_blank", "noopener");
  };
  useEffect(() => {
    api.get(`/workspaces/${wsId}/clients`).then((r) => setClients(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/projects`).then((r) => setProjects(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => {});
  }, [wsId]);
  const toggleProject = (pid) => set("project_ids", form.project_ids.includes(pid) ? form.project_ids.filter((x) => x !== pid) : [...form.project_ids, pid]);
  const toggleMember = (uid) => set("assigned_user_ids", form.assigned_user_ids.includes(uid) ? form.assigned_user_ids.filter((x) => x !== uid) : [...form.assigned_user_ids, uid]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/brands/${brand.id}`, { ...form, client_id: form.client_id || null });
      toast.success("Saved");
      await onSaved?.();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Archive & delete brand "${brand.name}"?`)) return;
    try {
      await api.delete(`/brands/${brand.id}`);
      toast.success("Brand deleted");
      window.location.href = "/app/brands";
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="brand-settings-dialog">
        <DialogHeader><DialogTitle>Brand settings</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-10 rounded-none" data-testid="brand-set-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Industry</Label>
              <Input value={form.industry} onChange={(e) => set("industry", e.target.value)} className="h-10 rounded-none" data-testid="brand-set-industry" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="brand-set-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Website</Label>
              <Input value={form.website} onChange={(e) => set("website", e.target.value)} className="h-10 rounded-none" data-testid="brand-set-website" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Logo URL</Label>
              <div className="flex items-center gap-2">
                <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} className="h-10 rounded-none flex-1" data-testid="brand-set-logo" />
                {/* Iter 253w · File upload — for admins who don't have hosting handy. */}
                <label className="inline-flex items-center gap-1 h-10 px-2 border border-border cursor-pointer bg-background hover:bg-muted text-xs whitespace-nowrap"
                        data-testid="brand-logo-upload-label">
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingLogo ? "…" : "Upload"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="sr-only"
                          onChange={(e) => uploadLogo(e.target.files?.[0])}
                          data-testid="brand-logo-upload-input" />
                </label>
              </div>
            </div>
          </div>
          {/* Iter 253v · Portal theming — color + tagline live-preview the client-portal signup card. */}
          <div className="border border-border p-3 space-y-3 bg-muted/30" data-testid="brand-portal-theme">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Client-portal theming</div>
            <div className="grid grid-cols-[auto,1fr] items-end gap-3">
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Primary colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.primary_color}
                         onChange={(e) => set("primary_color", e.target.value)}
                         className="h-10 w-14 p-0 border border-border cursor-pointer rounded-none"
                         data-testid="brand-set-primary-color-picker" />
                  <Input value={form.primary_color}
                         onChange={(e) => set("primary_color", e.target.value)}
                         className="h-10 w-28 rounded-none font-mono text-xs"
                         data-testid="brand-set-primary-color" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Portal tagline (optional)</Label>
                <Input value={form.portal_tagline}
                       onChange={(e) => set("portal_tagline", e.target.value)}
                       placeholder="e.g. Your studio's client hub" className="h-10 rounded-none"
                       data-testid="brand-set-portal-tagline" />
              </div>
            </div>
            {/* Preview card — kept small so admins see the impact without opening a new tab. */}
            <div className="flex items-start gap-3 flex-wrap">
              <div className="border border-border bg-white overflow-hidden max-w-xs flex-1" data-testid="brand-portal-preview">
                <div className="h-1" style={{ background: form.primary_color }} />
                <div className="p-3 flex items-center gap-2">
                  {form.logo_url
                    ? <img src={form.logo_url} alt="" className="h-6 w-auto object-contain" />
                    : <div className="h-6 w-6 flex items-center justify-center text-xs font-bold text-white"
                            style={{ background: form.primary_color }}>{(form.name || "S")[0]}</div>}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">{form.name || "Your brand"}</div>
                    <div className="text-[9px] text-muted-foreground truncate">
                      {form.portal_tagline || "Client portal"}
                    </div>
                  </div>
                </div>
                <button type="button" className="w-full text-[10px] font-semibold text-white py-1.5"
                        style={{ background: form.primary_color }}>Sign in preview</button>
              </div>
              <button type="button" onClick={openPortalPreview}
                       className="h-10 px-3 text-xs font-medium border border-border bg-background hover:bg-muted inline-flex items-center gap-1"
                       data-testid="brand-portal-preview-open">
                <ExternalLink className="w-3.5 h-3.5" /> View live portal
              </button>
            </div>
          </div>
          {/* Iter 253x · Portal custom domain — brand can map their own subdomain */}
          <BrandCustomDomainBlock brand={brand} />
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Client (CRM)</Label>
            <Select value={form.client_id || "_none"} onValueChange={(v) => set("client_id", v === "_none" ? "" : v)}>
              <SelectTrigger className="h-10 rounded-none" data-testid="brand-set-client"><SelectValue placeholder="No client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No client</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Linked projects ({form.project_ids.length})</Label>
            <div className="border border-border max-h-40 overflow-y-auto divide-y divide-border">
              {projects.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-muted/40">
                  <input type="checkbox" checked={form.project_ids.includes(p.id)} onChange={() => toggleProject(p.id)} data-testid={`brand-set-project-${p.id}`} />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
              {projects.length === 0 && <div className="px-2.5 py-2 text-xs text-muted-foreground">No projects in workspace.</div>}
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Assigned team members ({form.assigned_user_ids.length})</Label>
            <p className="text-[11px] text-muted-foreground mb-2">Individual contributors can only see brands they&apos;re assigned to. Managers with the <code className="font-mono">brand.manage</code> permission always see everything.</p>
            <div className="border border-border max-h-40 overflow-y-auto divide-y divide-border">
              {members.map((m) => (
                <label key={m.user_id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-muted/40">
                  <input type="checkbox" checked={form.assigned_user_ids.includes(m.user_id)} onChange={() => toggleMember(m.user_id)} data-testid={`brand-set-member-${m.user_id}`} />
                  <span className="truncate flex-1">{m.user?.name || m.user?.email || m.user_id}</span>
                  {m.user?.email && <span className="text-[10px] text-muted-foreground truncate">{m.user.email}</span>}
                </label>
              ))}
              {members.length === 0 && <div className="px-2.5 py-2 text-xs text-muted-foreground">No members in workspace.</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="destructive" className="rounded-none mr-auto" onClick={del} data-testid="brand-set-delete"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="brand-set-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Iter 253x · Custom domain manager — separate PATCH endpoint so the main
// Save action doesn't accidentally clear the mapping. Also validates via
// the resolve endpoint so admins get instant CNAME feedback.
function BrandCustomDomainBlock({ brand }) {
  const [domain, setDomain] = useState(brand.custom_domain || "");
  const [saved, setSaved] = useState(brand.custom_domain || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const clean = (domain || "").trim().toLowerCase();
    setBusy(true);
    try {
      await api.patch(`/brands/${brand.id}/custom-domain`, {
        custom_domain: clean || null,
      });
      setSaved(clean);
      toast.success(clean ? `Domain mapped to ${clean}` : "Custom domain cleared");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const platformHost = typeof window !== "undefined" ? window.location.host : "acquilix.app";
  return (
    <div className="border border-border p-3 space-y-2 bg-muted/30" data-testid="brand-custom-domain">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">
        Client-portal custom domain
      </div>
      <p className="text-[11px] text-muted-foreground">
        Point a subdomain you own (e.g. <code className="font-mono">support.acmecoffee.com</code>) at
        the platform via DNS <code className="font-mono">CNAME → {platformHost}</code>, then paste
        it here. Visitors to that subdomain get this brand&apos;s themed portal.
      </p>
      <div className="flex items-center gap-2">
        <Input value={domain} onChange={(e) => setDomain(e.target.value)}
                placeholder="support.acmecoffee.com"
                className="h-9 rounded-none flex-1 font-mono text-xs"
                data-testid="brand-custom-domain-input" />
        <Button size="sm" className="rounded-none h-9" onClick={save} disabled={busy || domain === saved}
                 data-testid="brand-custom-domain-save">
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
      {saved && (
        <div className="text-[10px] text-emerald-700 font-mono">
          ✓ mapped: {saved}
        </div>
      )}
    </div>
  );
}
